/**
 * Правила «Моих отзывов» и кнопки отзыва в заказе. Модели `@repo/api` здесь не импортируются
 * (декораторы не загружаются в `bun test`): статусы сравниваются со строковыми значениями
 * `ReviewStatus` бэкенда.
 */

/** Что показать у позиции заказа: ничего, «Оценить товар» или ссылку «Мой отзыв». */
export type OrderItemReviewAction = 'none' | 'write' | 'view'

/**
 * Оценить можно только полученную позицию (`OrderItemStatus.Completed`) и только при
 * включённом модуле; если отзыв на товар уже есть — вместо кнопки ссылка на «Мои отзывы».
 */
export function orderItemReviewAction(opts: { enabled: boolean, completed: boolean, reviewed: boolean }): OrderItemReviewAction {
  if (!opts.enabled || !opts.completed) return 'none'
  return opts.reviewed ? 'view' : 'write'
}

export type MyReviewStatusView = {
  color: 'warning' | 'success' | 'error' | 'neutral'
  /** Отклонён — показать причину и комментарий модератора */
  rejected: boolean
  /** Пояснение под статусом */
  hint?: string
}

/** Оформление статуса модерации своего отзыва; подпись статуса — из словаря бэкенда. */
export function myReviewStatusView(status: string): MyReviewStatusView {
  switch (status) {
    case 'Pending':
      return { color: 'warning', rejected: false, hint: 'Отзыв появится на странице товара после проверки' }
    case 'Published':
      return { color: 'success', rejected: false }
    case 'Rejected':
      return { color: 'error', rejected: true }
    default:
      return { color: 'neutral', rejected: false }
  }
}

/**
 * Отметки «оценено в этой сессии» — список productId (см. `useReviewedNow`).
 * Функции возвращают новый список: состояние хранится в `useState` и заменяется целиком.
 */
export function markProductReviewed(marks: readonly number[], productId: number): number[] {
  return marks.includes(productId) ? [...marks] : [...marks, productId]
}

export function unmarkProductReviewed(marks: readonly number[], productId: number): number[] {
  return marks.filter(id => id !== productId)
}

/** Есть ли отзыв на товар: с сервера (`reviewId`) или оставлен в этой сессии. */
export function isProductReviewed(marks: readonly number[], productId: number, reviewId: number | null | undefined): boolean {
  return !!reviewId || marks.includes(productId)
}

/** Отметки принадлежат покупателю: при выходе и входе они сбрасываются. */
export function resetReviewedMarks(): number[] {
  return []
}

/**
 * Текст после отправки формы — по статусу из ответа `create`/`update` (`ShowcaseMyReviewDto.status`):
 * при премодерации отзыв ещё не виден на странице товара, и покупатель должен это знать.
 * Без статуса (при правке сменился только флаг имени) — нейтральный текст.
 */
export function reviewSubmittedMessage(kind: 'create' | 'edit', status: string | null | undefined): string {
  const pending = status === 'Pending'
  if (kind === 'create') return pending ? 'Спасибо! Отзыв появится после проверки' : 'Спасибо за отзыв!'
  return pending ? 'Изменения появятся после проверки' : 'Отзыв сохранён'
}

/** Название товара в «Моих отзывах»: бэкенд отдаёт пустую строку, если названия нет нигде. */
export function myReviewProductTitle(name: string | null | undefined): string {
  return name?.trim() || 'Товар'
}
